"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, AgGridTable, Badge, Section, DropdownMenu } from "@/src/components";
import type { ColDef, GridOptions } from "ag-grid-community";
import { MoreHorizontal } from "lucide-react";
import { initAgGridLicense } from "@/lib/ag-grid-license";

// Initialize AG Grid license
initAgGridLicense(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

type User = {
  id: string;
  name: string;
  email: string;
  role: string;
  status: "active" | "inactive" | "pending";
};

const sampleData: User[] = [
  { id: "1", name: "John Doe", email: "john@example.com", role: "Admin", status: "active" },
  { id: "2", name: "Jane Smith", email: "jane@example.com", role: "Editor", status: "active" },
  { id: "3", name: "Bob Wilson", email: "bob@example.com", role: "Viewer", status: "inactive" },
  { id: "4", name: "Alice Brown", email: "alice@example.com", role: "Editor", status: "pending" },
  { id: "5", name: "Charlie Davis", email: "charlie@example.com", role: "Admin", status: "active" },
];

const columns: ColDef<User>[] = [
  {
    field: "id",
    headerName: "ID",
    width: 80,
    checkboxSelection: true,
    headerCheckboxSelection: true,
  },
  {
    field: "name",
    headerName: "Name",
    minWidth: 150,
    filter: "agTextColumnFilter",
  },
  {
    field: "email",
    headerName: "Email",
    minWidth: 200,
    filter: "agTextColumnFilter",
  },
  {
    field: "role",
    headerName: "Role",
    minWidth: 120,
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    cellRenderer: (params: { value: string }) => {
      const status = params.value;
      const variant = status === "active" ? "default" : status === "pending" ? "secondary" : "outline";
      return <Badge variant={variant}>{status}</Badge>;
    },
  },
  {
    colId: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    filter: false,
    cellRenderer: (params: { data: User }) => (
      <DropdownMenu
        trigger={{
          icon: <MoreHorizontal className="h-4 w-4" />,
          iconOnly: true,
          variant: "ghost",
        }}
        items={[
          { type: "item", label: "Edit", onClick: () => console.log("Edit", params.data?.id) },
          { type: "item", label: "View", onClick: () => console.log("View", params.data?.id) },
          { type: "separator" },
          { type: "item", label: "Delete", onClick: () => console.log("Delete", params.data?.id) },
        ]}
      />
    ),
  },
];

export default function DocsBlocksAgGrid() {
  const gridOptions = React.useMemo<GridOptions<User>>(() => ({
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
    },
    rowSelection: "multiple",
    suppressRowClickSelection: true,
  }), []);

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            AG Grid
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            An enterprise-grade data grid powered by AG Grid with sorting,
            filtering, pagination, and row selection.
          </Text>
        </div>
      </Section>

      {/* Features */}
      <Section>
        <SectionCard
          header={<CardTitle>Features</CardTitle>}
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">✓</Badge>
              <Text className="text-sm">Sorting</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">✓</Badge>
              <Text className="text-sm">Filtering</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">✓</Badge>
              <Text className="text-sm">Pagination</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">✓</Badge>
              <Text className="text-sm">Row Selection</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">✓</Badge>
              <Text className="text-sm">Column Resizing</Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">✓</Badge>
              <Text className="text-sm">Floating Filters</Text>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Live Demo */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Live Demo</CardTitle>
              <CardDescription>Interactive example with all features enabled</CardDescription>
            </>
          }
          contentClassName="p-2"
        >
          <AgGridTable
            columnDefs={columns}
            rowData={sampleData}
            height={300}
            gridOptions={gridOptions}
          />
        </SectionCard>
      </Section>

      {/* Basic Usage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Usage</CardTitle>
              <CardDescription>Minimum code to get started</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto space-y-1">
            <code className="block">{"import { AgGridTable } from \"@/registry/new-york/ui/ag-grid-table\";"}</code>
            <code className="block">{"import type { ColDef } from \"ag-grid-community\";"}</code>
            <code className="block mt-4">{"type User = {"}</code>
            <code className="block pl-4">{"id: string;"}</code>
            <code className="block pl-4">{"name: string;"}</code>
            <code className="block pl-4">{"email: string;"}</code>
            <code className="block">{"};"}</code>
            <code className="block mt-4">{"const columns: ColDef<User>[] = ["}</code>
            <code className="block pl-4">{"{ field: \"id\", headerName: \"ID\" },"}</code>
            <code className="block pl-4">{"{ field: \"name\", headerName: \"Name\" },"}</code>
            <code className="block pl-4">{"{ field: \"email\", headerName: \"Email\" },"}</code>
            <code className="block">{"];"}</code>
            <code className="block mt-4">{"<AgGridTable"}</code>
            <code className="block pl-4">{"columnDefs={columns}"}</code>
            <code className="block pl-4">{"rowData={users}"}</code>
            <code className="block pl-4">{"height={400}"}</code>
            <code className="block pl-4">{"gridOptions={{"}</code>
            <code className="block pl-8">{"defaultColDef: { sortable: true, filter: true },"}</code>
            <code className="block pl-4">{"}}"}</code>
            <code className="block">{"/>"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Custom Cell Rendering */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Custom Cell Rendering</CardTitle>
              <CardDescription>Render custom content in table cells</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto space-y-1">
            <code className="block">{"{"}</code>
            <code className="block pl-4">{"field: \"status\","}</code>
            <code className="block pl-4">{"headerName: \"Status\","}</code>
            <code className="block pl-4">{"cellRenderer: (params) => {"}</code>
            <code className="block pl-8">{"const status = params.value;"}</code>
            <code className="block pl-8">{"return <Badge variant={status === \"active\" ? \"default\" : \"secondary\"}>{status}</Badge>;"}</code>
            <code className="block pl-4">{"},"}</code>
            <code className="block">{"}"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Props Table */}
      <Section>
        <SectionCard
          header={<CardTitle>Props</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Prop</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Default</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">columnDefs</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ColDef[]</td>
                <td className="py-2 font-mono text-xs">required</td>
                <td className="py-2 text-muted-foreground">Column definitions</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">rowData</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">TData[]</td>
                <td className="py-2 font-mono text-xs">required</td>
                <td className="py-2 text-muted-foreground">Table data array</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">gridOptions</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">GridOptions</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">AG Grid configuration options</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">height</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">number | string</td>
                <td className="py-2 font-mono text-xs">500</td>
                <td className="py-2 text-muted-foreground">Grid height in pixels</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">header</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Optional toolbar above grid</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">className</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Additional CSS classes</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>

      {/* Accessibility */}
      <Section>
        <SectionCard
          header={<CardTitle>Accessibility</CardTitle>}
          contentClassName="p-6 space-y-4"
        >
          <ul className="space-y-2 text-sm">
            <li>• <strong>Keyboard navigation:</strong> Full keyboard support for cells and rows</li>
            <li>• <strong>Screen readers:</strong> ARIA roles and labels for grid elements</li>
            <li>• <strong>Focus management:</strong> Proper focus indicators and tab navigation</li>
            <li>• <strong>Sortable columns:</strong> Sort state announced to screen readers</li>
            <li>• <strong>Selection state:</strong> Checkbox states announced to screen readers</li>
          </ul>
        </SectionCard>
      </Section>
    </>
  );
}
